import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, PointerEvent } from 'react'

// Collapsed launcher is a 48px circle (see AskRemyLauncher.styles). The hover
// pill grows sideways from this circle, so all positioning math uses the
// collapsed size and lets the anchor side decide where the pill expands to.
export const LAUNCHER_SIZE = 48
// Matches the `right`/`bottom` the stylesheet applies before any drag.
export const DEFAULT_EDGE_OFFSET = 24
// Once dragged, the launcher stays at least this far from every viewport edge.
export const VIEWPORT_MARGIN = 5
export const POSITION_STORAGE_KEY = 'askRemyLauncher.position'

// A press that moves less than this is a click, not a drag.
const DRAG_THRESHOLD = 5

type LauncherPointerEvent = Pick<
  PointerEvent<HTMLElement>,
  'clientX' | 'clientY' | 'pointerId' | 'currentTarget'
>

export type HorizontalAnchor = 'left' | 'right'
export type VerticalAnchor = 'top' | 'bottom'

// Where the collapsed circle rests, measured from the viewport corner nearest
// to it: `x` from the `horizontal` edge and `y` from the `vertical` edge.
// Anchoring to the nearest corner keeps the launcher beside that corner when
// the window is resized, instead of drifting away with a fixed offset.
export interface LauncherPosition {
  horizontal: HorizontalAnchor
  vertical: VerticalAnchor
  x: number
  y: number
}

// Viewport coordinates of the collapsed circle's top-left corner; only used
// while translating pointer deltas into a new anchored position.
interface AbsolutePosition {
  left: number
  top: number
}

const DEFAULT_POSITION: LauncherPosition = {
  horizontal: 'right',
  vertical: 'bottom',
  x: DEFAULT_EDGE_OFFSET,
  y: DEFAULT_EDGE_OFFSET,
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max))

const maxLeft = () => window.innerWidth - LAUNCHER_SIZE - VIEWPORT_MARGIN
const maxTop = () => window.innerHeight - LAUNCHER_SIZE - VIEWPORT_MARGIN

// Keeps the anchor and pulls the offsets back inside the viewport.
const clampToViewport = (position: LauncherPosition): LauncherPosition => ({
  ...position,
  x: clamp(position.x, VIEWPORT_MARGIN, maxLeft()),
  y: clamp(position.y, VIEWPORT_MARGIN, maxTop()),
})

const toAbsolute = ({ horizontal, vertical, x, y }: LauncherPosition): AbsolutePosition => ({
  left: horizontal === 'left' ? x : window.innerWidth - x - LAUNCHER_SIZE,
  top: vertical === 'top' ? y : window.innerHeight - y - LAUNCHER_SIZE,
})

// Clamps to the viewport (minus the margin), then anchors to whichever corner
// the circle's center is nearest. Horizontally this also makes the hover pill
// expand toward the viewport's center and never past the edge.
const toAnchored = (absolute: AbsolutePosition): LauncherPosition => {
  const left = clamp(absolute.left, VIEWPORT_MARGIN, maxLeft())
  const top = clamp(absolute.top, VIEWPORT_MARGIN, maxTop())
  const horizontal = left + LAUNCHER_SIZE / 2 < window.innerWidth / 2 ? 'left' : 'right'
  const vertical = top + LAUNCHER_SIZE / 2 < window.innerHeight / 2 ? 'top' : 'bottom'
  return {
    horizontal,
    vertical,
    x: horizontal === 'left' ? left : window.innerWidth - left - LAUNCHER_SIZE,
    y: vertical === 'top' ? top : window.innerHeight - top - LAUNCHER_SIZE,
  }
}

const isLauncherPosition = (value: unknown): value is LauncherPosition => {
  if (typeof value !== 'object' || value === null) return false
  const { horizontal, vertical, x, y } = value as Partial<LauncherPosition>
  return (
    (horizontal === 'left' || horizontal === 'right') &&
    (vertical === 'top' || vertical === 'bottom') &&
    Number.isFinite(x) &&
    Number.isFinite(y)
  )
}

const readStoredPosition = (): LauncherPosition | null => {
  try {
    const raw = window.localStorage.getItem(POSITION_STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return isLauncherPosition(parsed) ? clampToViewport(parsed) : null
  } catch {
    return null
  }
}

const storePosition = (position: LauncherPosition) => {
  try {
    window.localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position))
  } catch {
    // Storage may be full or disabled; the position still applies for this session.
  }
}

// Pins the anchored edges and releases the opposite ones so the stylesheet's
// default corner no longer applies.
const toStyle = (position: LauncherPosition | null): CSSProperties | undefined => {
  if (!position) return undefined
  const { horizontal, vertical, x, y } = position
  return {
    left: horizontal === 'left' ? x : 'auto',
    right: horizontal === 'right' ? x : 'auto',
    top: vertical === 'top' ? y : 'auto',
    bottom: vertical === 'bottom' ? y : 'auto',
  }
}

// Makes the floating launcher draggable anywhere in the viewport (minus a
// margin) while keeping a plain click as the way to open the chat. Spread
// `dragHandlers` on the button and apply `style`; `position` is null until the
// user drags, so the stylesheet's default corner applies. The last position
// survives reloads through localStorage.
export function useDraggableLauncher(onActivate: () => void) {
  const [position, setPosition] = useState<LauncherPosition | null>(readStoredPosition)
  const [isDragging, setIsDragging] = useState(false)

  const isPressed = useRef(false)
  const hasMoved = useRef(false)
  const suppressClick = useRef(false)
  const pressStart = useRef({ x: 0, y: 0 })
  const positionAtPress = useRef<AbsolutePosition>({ left: 0, top: 0 })
  const latestPosition = useRef(position)
  latestPosition.current = position

  useEffect(() => {
    const onResize = () => setPosition(current => current && clampToViewport(current))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const onPointerDown = useCallback((event: LauncherPointerEvent) => {
    isPressed.current = true
    hasMoved.current = false
    suppressClick.current = false
    pressStart.current = { x: event.clientX, y: event.clientY }
    positionAtPress.current = toAbsolute(latestPosition.current ?? DEFAULT_POSITION)
    // Keep receiving moves even when the pointer outruns the button.
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }, [])

  const onPointerMove = useCallback((event: LauncherPointerEvent) => {
    if (!isPressed.current) return

    const dx = event.clientX - pressStart.current.x
    const dy = event.clientY - pressStart.current.y

    if (!hasMoved.current) {
      if (Math.abs(dx) <= DRAG_THRESHOLD && Math.abs(dy) <= DRAG_THRESHOLD) return
      hasMoved.current = true
      setIsDragging(true)
    }

    const next = toAnchored({
      left: positionAtPress.current.left + dx,
      top: positionAtPress.current.top + dy,
    })
    latestPosition.current = next
    setPosition(next)
  }, [])

  const endPress = useCallback((event: LauncherPointerEvent) => {
    if (!isPressed.current) return
    isPressed.current = false
    event.currentTarget.releasePointerCapture?.(event.pointerId)

    if (hasMoved.current) {
      // The browser still fires `click` after a drag; skip that one.
      suppressClick.current = true
      setIsDragging(false)
      if (latestPosition.current) storePosition(latestPosition.current)
    }
  }, [])

  const onClick = useCallback(() => {
    if (suppressClick.current) {
      suppressClick.current = false
      return
    }
    onActivate()
  }, [onActivate])

  return {
    position,
    style: toStyle(position),
    isDragging,
    dragHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endPress,
      onPointerCancel: endPress,
      onClick,
    },
  }
}
